import { trackEvent } from "./telemetry";

// Every message the journey sends to the page that embeds or opened it
// goes through postToPublisher, which posts exactly as the call sites
// did before and then records a "publisher_message" telemetry event.
// Without this the database looks identical whether a session told its
// publisher PASS or FAIL (or nothing), so a stuck integration could not
// be diagnosed per session.
//
// The post always happens first. Logging runs after it and never throws,
// so telemetry can not block, reorder or delay a message.

export const PUBLISHER_MESSAGE_EVENT = "publisher_message";

const safeString = (v) => (typeof v === "string" ? v : v == null ? null : String(v));

function embedderHost() {
  try {
    if (typeof document === "undefined" || !document.referrer) return null;
    return new URL(document.referrer).hostname || null;
  } catch {
    return null;
  }
}

function isEmbedded() {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top can throw; that only happens
    // when we are framed.
    return true;
  }
}

// Describe a message without copying its contents. Object payloads can
// carry identity data (the passkey bypass payload spreads user fields),
// so only known status fields are kept.
export function describeMessage(message) {
  if (typeof message === "string") return { message };
  if (!message || typeof message !== "object") return { message: safeString(message) };

  if (message.messageType === "payload") {
    const v = message.value || {};
    return {
      message: "payload",
      payload_event: safeString(v.event),
      payload_status: safeString(v.status),
      payload_app: safeString(v.app),
      payload_error: safeString(v.error),
    };
  }

  const out = { message: safeString(message.messageType || message.type || "object") };
  if (message.status !== undefined) out.payload_status = safeString(message.status);
  if (typeof message.success === "boolean") out.payload_success = message.success;
  return out;
}

export function publisherMessageMetadata(message, opts = {}) {
  const meta = {
    ...describeMessage(message),
    target: opts.target || "parent",
    trigger: opts.trigger || "unspecified",
    embedded: isEmbedded(),
    embedder_host: embedderHost(),
  };
  if (opts.reviewHold !== undefined) meta.review_hold = opts.reviewHold === true;
  if (opts.publisherStatusSetting !== undefined) {
    meta.publisher_status_setting = opts.publisherStatusSetting ?? null;
  }
  return meta;
}

export function logPublisherMessage(message, opts = {}) {
  try {
    trackEvent(PUBLISHER_MESSAGE_EVENT, publisherMessageMetadata(message, opts));
  } catch {
    // Telemetry is best-effort and must never affect the journey.
  }
}

// target: "parent" (default) | "opener" | "react_native"
// trigger: why the message was sent (status_terminal, done_button, ...)
// reviewHold / publisherStatusSetting: set when the message was derived
// from a review hold and reviewHoldPublisherStatus.
export function postToPublisher(message, opts = {}) {
  const { target = "parent", targetOrigin = "*" } = opts;
  if (target === "react_native") {
    const data = typeof message === "string" ? message : JSON.stringify(message);
    window.ReactNativeWebView.postMessage(data);
  } else if (target === "opener") {
    // No opener means nothing was sent, so nothing is logged.
    if (!window.opener) return;
    window.opener.postMessage(message, targetOrigin);
  } else {
    window.parent.postMessage(message, targetOrigin);
  }
  logPublisherMessage(message, { ...opts, target });
}
